// Vista em lista do Pipeline.
//
// Redesenho de 01/10/2026, no padrão das listas de negócio dos CRMs de
// referência (HubSpot, Pipedrive):
//
//  • **Toda coluna que é número ou data ordena** — valor, probabilidade, dias
//    sem movimento, abertura. Antes a ordem era só a que o servidor mandava.
//  • **Colunas novas: "Sem movimento" e "Próximo passo"** — as duas perguntas
//    que a lista não respondia: "o que está parado?" e "o que tem dono e data?".
//  • **Rodapé com o total do que está na tela** (quantidade, valor, ponderado):
//    filtrou um cliente, a soma dele está embaixo.
//  • **Etapa colorida** e dono em avatar; o cabeçalho fica preso ao rolar.
//  • **Ações secundárias num menu "⋯"**: eram quatro botões por linha em 479
//    linhas. Ficam à vista só Espiar e Acompanhar.
//
// Extraída de ComercialPipeline.tsx (a página é "entry fino" desde 2026-05-23).
import { Button, Select, Tag, Table, Typography, Space, Empty, Dropdown, Tooltip } from 'antd';
import {
  FileTextOutlined, DeleteOutlined, BarChartOutlined, EyeOutlined, MoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Proposta } from './types';
import { fmt, STATUS_LABELS, STATUS_COLORS } from './utils';
import {
  diasSemMovimento, ehAberto, valorDoNegocio, valorPonderado, type ProximoPasso,
} from './saudeNegocio';
import { AvatarDono, SeloParado, SeloPasso } from './pecas';
import { fmtDataBr } from '../../utils/dataFmt';
// Radar pessoal (22/09/2026): acompanhar um negócio privado como se acompanha
// um edital — o pedido do Gustavo de mapear "propostas que funcionam como
// licitação, porém são privadas".
import { BotaoAcompanharNegocio } from '../meu-radar/BotaoAcompanharNegocio';

const { Text } = Typography;

const OPCOES_STATUS = Object.entries(STATUS_LABELS).map(([k, v]) => ({ label: v, value: k }));

export function ListaPipeline({
  propostas, passos, loading, onAbrir, onEspiar, onMudarStatus, onIrComposicao, onIrPropostas, onExcluir,
}: {
  propostas: Proposta[];
  passos: Record<number, ProximoPasso>;
  loading: boolean;
  onAbrir: (p: Proposta) => void;
  onEspiar: (p: Proposta) => void;
  /** Sem ele (sem permissão de mudar etapa), o seletor de etapa fica travado. */
  onMudarStatus?: (id: number, novo: string) => void;
  onIrComposicao: (p: Proposta) => void;
  onIrPropostas: (p: Proposta) => void;
  onExcluir: (id: number) => void;
}) {
  // Negócio fechado mostra "—" na coluna: na ordenação ele vale -1, senão os
  // ganhos de 2025 apareceriam como "os mais parados".
  const idle = (p: Proposta) => {
    if (!ehAberto(p.status)) return -1;
    const d = diasSemMovimento(p);
    return d == null ? -1 : d;
  };

  const cols: ColumnsType<Proposta> = [
    {
      title: 'Negócio', dataIndex: 'titulo', key: 't',
      sorter: (a, b) => (a.titulo || '').localeCompare(b.titulo || '', 'pt-BR'),
      render: (v, p) => (
        <div style={{ minWidth: 0 }}>
          <Button type="link" size="small" onClick={() => onAbrir(p)}
            style={{ padding: 0, height: 'auto', color: '#0930D0', fontWeight: 600, whiteSpace: 'normal',
              textAlign: 'left', lineHeight: 1.3 }}>
            {v}
          </Button>
          {p.origem === 'licitacao' && (
            <Tag color="purple" style={{ fontSize: 9, marginLeft: 6, padding: '0 4px', lineHeight: '14px' }}>Licitação</Tag>
          )}
          <div style={{ fontSize: 11, color: '#5A6378', marginTop: 1 }}>
            {p.cliente_nome || <em>sem empresa</em>}
            {p.contato_nome && <span style={{ color: '#9AA0B4' }}> · {p.contato_nome}</span>}
          </div>
        </div>
      ),
    },
    {
      title: 'Etapa', dataIndex: 'status', key: 's', width: 182,
      render: (s, p) => {
        const cor = STATUS_COLORS[s] || STATUS_COLORS.prospeccao;
        return (
          <Select value={s} size="small" variant="borderless" showSearch optionFilterProp="label"
            popupMatchSelectWidth={false} options={OPCOES_STATUS}
            disabled={!onMudarStatus}
            onChange={(v) => onMudarStatus?.(p.id, v)} onClick={e => e.stopPropagation()}
            style={{ width: '100%', background: cor.bg, borderRadius: 4, color: cor.color, fontWeight: 600 }}
            labelRender={(o) => <span style={{ color: cor.color, fontWeight: 600, fontSize: 12 }}>{o.label}</span>} />
        );
      },
    },
    {
      title: 'Valor', key: 've', align: 'right', width: 118,
      sorter: (a, b) => valorDoNegocio(a) - valorDoNegocio(b), defaultSortOrder: undefined,
      render: (_, p) => (
        <Text strong style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(valorDoNegocio(p))}</Text>
      ),
    },
    {
      title: 'Prob.', dataIndex: 'probabilidade', key: 'pr', align: 'right', width: 64,
      sorter: (a, b) => (Number(a.probabilidade) || 0) - (Number(b.probabilidade) || 0),
      render: (v, p) => ehAberto(p.status) && v != null
        ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}%</span>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Responsável', dataIndex: 'responsavel', key: 'r', width: 156,
      sorter: (a, b) => (a.responsavel || '').localeCompare(b.responsavel || '', 'pt-BR'),
      render: (v) => (
        <Space size={6} style={{ minWidth: 0 }}>
          <AvatarDono nome={v} tamanho={20} />
          <Text ellipsis style={{ maxWidth: 108, fontSize: 12, color: v ? undefined : '#9AA0B4' }}>
            {v || 'sem responsável'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Próximo passo', key: 'pp', width: 136,
      render: (_, p) => <SeloPasso negocio={p} passo={passos[p.id]} />,
    },
    {
      title: (
        <Tooltip title="Dias desde a última vez que alguém mexeu no negócio">Sem movimento</Tooltip>
      ),
      key: 'sm', width: 122, align: 'right',
      sorter: (a, b) => idle(a) - idle(b),
      render: (_, p) => ehAberto(p.status) ? <SeloParado negocio={p} /> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Abertura', dataIndex: 'data_abertura', key: 'da', width: 94, align: 'right',
      sorter: (a, b) => String(a.data_abertura || '').localeCompare(String(b.data_abertura || '')),
      render: (v) => v ? <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{fmtDataBr(v)}</Text> : '—',
    },
    {
      title: '', key: 'ac', width: 100, align: 'right', fixed: 'right',
      render: (_, p) => (
        <Space size={2}>
          <Tooltip title="Espiar sem sair da lista">
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => onEspiar(p)} aria-label="Espiar" />
          </Tooltip>
          <BotaoAcompanharNegocio negocioId={p.id} titulo={p.titulo} />
          <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: [
            { key: 'comp', icon: <BarChartOutlined />, label: 'Composições', onClick: () => onIrComposicao(p) },
            { key: 'prop', icon: <FileTextOutlined />, label: 'Propostas geradas', onClick: () => onIrPropostas(p) },
            { type: 'divider' as const },
            { key: 'del', icon: <DeleteOutlined />, label: 'Excluir', danger: true, onClick: () => onExcluir(p.id) },
          ] }}>
            <Button size="small" type="text" icon={<MoreOutlined />} aria-label="Mais ações" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const total = propostas.reduce((a, p) => a + valorDoNegocio(p), 0);
  const ponderado = propostas.reduce((a, p) => a + valorPonderado(p), 0);

  return (
    <Table rowKey="id" columns={cols} dataSource={propostas} loading={loading}
      size="small" sticky showSorterTooltip={false}
      pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} negócios` }}
      onRow={(p) => p.origem === 'licitacao' ? { style: { background: '#FBF9FF' } } : {}}
      locale={{ emptyText: <Empty description="Nenhum negócio encontrado com estes filtros." /> }}
      scroll={{ x: 1160 }}
      footer={() => (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: '#5A6378' }}>
          <span><b style={{ color: '#0E1433' }}>{propostas.length}</b> negócio{propostas.length === 1 ? '' : 's'} na lista</span>
          <span>Valor <b style={{ color: '#0E1433', fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</b></span>
          {ponderado > 0 && (
            <span>Ponderado (só abertos) <b style={{ color: '#0E1433', fontVariantNumeric: 'tabular-nums' }}>{fmt(ponderado)}</b></span>
          )}
        </div>
      )}
    />
  );
}
